import React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
	getBroadWatchGroups,
	addDiscordUserCardWatch,
	updateDiscordUserCardWatch,
	deleteDiscordUserCardWatch
} from './discordCardActions';
import { mergeWatchSets } from './watchCatalog';

export default function MoreWatches({
	guid,
	collection,
	rows,
	busy,
	atLimit,
	ready,
	onBusy,
	onNotice
}) {
	const catalog = useQuery('broad_watch_groups', getBroadWatchGroups);
	const queryClient = useQueryClient();
	const options = (Array.isArray(catalog.data) ? catalog.data : []).filter(
		option => Number(option.category) === collection.category
	);
	const findWatch = option =>
		rows.find(row =>
			(Array.isArray(row.sets) ? row.sets : []).some(
				set =>
					set.watch_scope === 'group' &&
					set.group_id === option.id &&
					Number(set.tcg_category) === collection.category
			)
		);
	const toggle = async (option, enabled) => {
		if (busy || !ready) return;
		const existing = findWatch(option);
		if (enabled && !existing && atLimit) return;
		const name = existing
			? existing.card_name
			: `Group: ${collection.category}:${option.id}`;
		const variant = {
			card_id: `group:${collection.category}:${option.id}`,
			watch_scope: 'group',
			group_id: option.id,
			tcg_category: collection.category,
			set_name: option.name
		};
		const removed = existing
			? existing.sets.filter(
					set =>
						set.watch_scope === 'group' &&
						set.group_id === option.id &&
						Number(set.tcg_category) === collection.category
			  )
			: [];
		const sets = mergeWatchSets(
			rows,
			name,
			enabled ? [variant] : [],
			removed
		);
		onBusy(true);
		try {
			if (existing && !sets.length)
				await deleteDiscordUserCardWatch(guid, name);
			else
				await (existing
					? updateDiscordUserCardWatch
					: addDiscordUserCardWatch)(guid, name, sets);
			queryClient.setQueryData(['discord_user_cards', guid], old => {
				const previous = Array.isArray(old) ? old : [];
				return [
					...previous.filter(row => row.card_name !== name),
					...(sets.length ? [{ card_name: name, sets }] : [])
				];
			});
			onNotice({
				text: `${option.name} ${
					enabled ? 'added to' : 'removed from'
				} your watches.`
			});
			queryClient.invalidateQueries(['discord_user_cards', guid]);
		} catch (error) {
			onNotice({
				error: true,
				text: error.message || 'Could not save this watch.'
			});
		} finally {
			onBusy(false);
		}
	};
	return (
		<section className="more-watches" aria-labelledby="more-watches-title">
			<div className="more-watches-heading">
				<h1 id="more-watches-title">{collection.short} groups</h1>
				<p>
					Watch a broader group without adding each product. Choose
					any number of groups; changes save automatically.
				</p>
				<p className="muted">
					These follow the existing Discord channel rules, including
					condition, price, seller, and edition restrictions. Your
					notification settings apply, and overlapping matches send
					one alert.
				</p>
				{atLimit && (
					<p role="status">
						Your watch limit is reached. Remove a watch to add
						another group.
					</p>
				)}
			</div>
			<div className="broad-watch-options">
				{catalog.isLoading ? (
					<p role="status">Loading watch groups…</p>
				) : catalog.error ? (
					<div role="alert">
						<p>Could not load watch groups.</p>
						<button
							className="secondary-button"
							onClick={() => catalog.refetch()}
						>
							Try again
						</button>
					</div>
				) : (
					options.map(option => {
						const checked = !!findWatch(option);
						return (
							<label
								className={`broad-watch-option ${
									checked ? 'selected' : ''
								}`}
								key={option.id}
							>
								<input
									type="checkbox"
									checked={checked}
									disabled={
										busy || !ready || (!checked && atLimit)
									}
									onChange={event =>
										toggle(option, event.target.checked)
									}
								/>
								<span>
									<strong>{option.name}</strong>
									<span>{option.description}</span>
								</span>
							</label>
						);
					})
				)}
			</div>
		</section>
	);
}
